import { computed, ref } from 'vue';
import type { ComputedRef } from 'vue';
import {
  DEMO_PERSONA_NONE,
  clearDemoPersonaRefusal,
  hasStoredDemoPersona,
  readDemoPersona,
  wasDemoPersonaRefused,
  writeDemoPersona,
} from '../utils/demoPersona';

// The persona half of the onboarding store (#409), factored out because
// workspace-ui keeps an onboarding store of its own (richer: staged wizard
// answers, the AVV gate) and must wear the same stage the module UIs wear —
// one default-persona rule, one resolution rule, not two.

// The module keys a persona can hold a seat in — user-api's module vocabulary.
export type DemoStageModuleKey = 'ats' | 'time_tracking' | 'people' | 'apprenticeship';

// The nine UI languages a module labels its demo text in (ADR 0083 decision 3,
// docs/contracts/demo-cast.v5.md § "The module contract"). `en` is the one every
// label carries; the bar falls back to it for a language a label lacks.
export type DemoLocale = 'de' | 'en' | 'fr' | 'es' | 'nl' | 'pl' | 'sv' | 'it' | 'pt';
export type DemoLabel = Partial<Record<DemoLocale, string>>;

// One fictional colleague the real user can be seen as. `personId` is the cast
// id every module maps to its own seat; `modules` lists the seats — a module
// missing here means "this persona does not use it" (no launcher tile).
export type DemoStagePersona = {
  key: string;
  personId: string;
  firstName: string;
  lastName: string;
  email: string;
  // Her generated portrait (`/api/people/media/<fileId>`, root-relative), when the
  // cast gives her one; absent or null = the initials chip. Additive on the sheet.
  photoUrl?: string | null;
  modules: Partial<Record<DemoStageModuleKey, { roleKey: string }>>;
  // The persona's journey (#409 M4): its tasks as the modules declare them
  // (demo-cast.v5 § "The stage"). Absent on a sheet from before journeys existed.
  journey?: DemoJourneyTask[];
};

// Who checks a journey task: a seat module, or Lumi — an evaluator only, never a
// seat (demo-cast.v5 § "The stage"), so it is not a DemoStageModuleKey.
export type DemoJourneyModuleKey = DemoStageModuleKey | 'assistant';

// One task of a journey, as its module declares it (ADR 0083 decision 6): an
// opaque key (never shown), the module that checks it, its label in the nine
// languages, and where in that module it is done (null: the module's start).
export type DemoJourneyTask = { key: string; module: DemoJourneyModuleKey; label: DemoLabel | null; path: string | null };

// The persona sheet user-api serves while the tenant is in demo (`demo.stage`),
// composed by the worker from People's cast and the modules' stage parts.
export type DemoStage = {
  companyName: string;
  // The cast's data set (demo-cast.v5 § "Data sets").
  language: 'de' | 'en';
  defaultPersonaKey: string;
  // The demo's module set (ADR 0083 decision 1).
  modules: string[];
  personas: DemoStagePersona[];
  // Each module's blocked-reason labels: module key → reason → label.
  reasons: Record<string, Record<string, DemoLabel>>;
};

export type DemoPersonaStateDeps = {
  getTenantSlug: () => string | null | undefined;
  // The signed-in master user id — the choice is stamped with it, like the
  // active role. Without one nothing is read or written.
  getUserId: () => string | null | undefined;
  // The sheet, already gated on "demo active": a stale status from a tab that
  // outlived the demo must not keep a mask on.
  stage: ComputedRef<DemoStage | null>;
  // How to reload the page once the choice is stored; defaults to
  // window.location.reload. workspace-ui passes its own: from the first-run
  // fork the reload lands on the workspace root instead (the default persona
  // is applied by the poll that sees the seed finish — reloading the fork in
  // place would land on the exit gate).
  reload?: () => void;
};

export function createDemoPersonaState(deps: DemoPersonaStateDeps) {
  const doReload = deps.reload ?? (() => window.location.reload());

  // The choice lives in localStorage and changes only through setPersona (which
  // reloads) or the default below, so a version ref bumped by those keeps the
  // computed honest without a storage listener.
  const personaVersion = ref(0);
  const storedPersona = computed(() => {
    personaVersion.value;
    return readDemoPersona(deps.getTenantSlug(), deps.getUserId());
  });

  // The persona worn right now, resolved against the sheet: null when nothing
  // is stored yet, a pre-D2 `'none'` is still stored (migrated on the next
  // applyDefaultPersona), or the choice is someone the sheet no longer lists
  // (the module would refuse that value; the sheet is what both sides read).
  const activePersona = computed<DemoStagePersona | null>(() => {
    const sheet = deps.stage.value;
    const chosen = storedPersona.value;
    if (!sheet || !chosen || chosen === DEMO_PERSONA_NONE) return null;
    return sheet.personas.find((p) => p.personId === chosen) ?? null;
  });
  const isMasked = computed(() => activePersona.value !== null);

  // Choosing a persona is a whole-page decision: every view holds
  // data fetched as the previous identity, so the honest way to switch is to
  // store the choice and reload — the same move the active-role switch makes.
  // `leave`: go there instead of reloading in place (a persona with no seat in
  // this module, issue 1056 — reloaded here, the module would refuse her).
  function setPersona(value: string, leave?: () => void): void {
    const slug = deps.getTenantSlug();
    const userId = deps.getUserId();
    if (!slug || !userId) return;
    writeDemoPersona(slug, userId, value);
    personaVersion.value += 1;
    if (readDemoPersona(slug, userId) !== value.toLowerCase()) return;
    // A fresh choice may be refused anew; the tab's refusal memory is for the
    // automatic default, not for the user.
    clearDemoPersonaRefusal(slug);
    (leave ?? doReload)();
  }

  // A visitor who never chose lands on stage as the sheet's default persona: the
  // first demo screen already shows the fictional company through a colleague's
  // eyes rather than the owner's empty view. Written once, then reloaded once —
  // any stored persona (including one another account stamped, which
  // readDemoPersona has by then removed) means "there was a choice", so no
  // second attempt. A stored `'none'` is the one exception: the demo is always
  // seen through a persona now (#409 follow-ups, D2) and the bar no longer
  // offers "yourself", so a tab that still holds the sentinel from before is
  // migrated to the default the same way a first visit is. Never after a
  // refusal in this tab: the default has no seat everywhere, and re-masking a
  // tab a module just refused would cycle. Call after every status fetch.
  let defaultApplied = false;
  function applyDefaultPersona(): boolean {
    if (defaultApplied) return false;
    const sheet = deps.stage.value;
    const slug = deps.getTenantSlug();
    const userId = deps.getUserId();
    if (!sheet || !slug || !userId) return false;
    if (wasDemoPersonaRefused(slug)) return false;

    // Reading first lets a foreign stamp clear itself before the existence check.
    const stored = readDemoPersona(slug, userId);
    if (stored !== null && stored !== DEMO_PERSONA_NONE) return false;
    if (stored === null && hasStoredDemoPersona(slug)) return false;
    const fallback = sheet.personas.find((p) => p.key === sheet.defaultPersonaKey) ?? sheet.personas[0];
    if (!fallback) return false;
    defaultApplied = true;
    writeDemoPersona(slug, userId, fallback.personId);
    if (readDemoPersona(slug, userId) !== fallback.personId.toLowerCase()) return false;
    personaVersion.value += 1;
    doReload();
    return true;
  }

  return { activePersona, isMasked, setPersona, applyDefaultPersona };
}
